/* eslint-disable react/require-default-props */
import React, { memo, useCallback, useState } from 'react';
import { Box, Center, Icon, Spinner, Button, ButtonGroup } from '@chakra-ui/core';
import { RiDeleteBinFill } from 'react-icons/ri';
import { useTranslation } from 'react-i18next';
import { withEffectCardWrapper } from '../withEffectCardWrapper';
import { BackgroundImage } from '../videoEffectTypes';
import { VideoEffectStore } from '../store/videoEffectModel';

interface ThumbnailCardProps {
  image: BackgroundImage;
  isApplying: boolean;
  isLoading: boolean;
  isActive: boolean;
  onClick: () => void;
}

export const VideoBackgroundCard = withEffectCardWrapper(
  memo(function VideoBackgroundCard({ image, isLoading, isApplying, isActive, onClick }: ThumbnailCardProps) {
    const { t } = useTranslation('videoInterviewEffect');

    const deleteBackgroundImage = VideoEffectStore.useStoreActions((state) => state.deleteBackgroundImage);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const requestDelete = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        // istanbul ignore next
        if (isDeleting || isActive) return; // Block trigger if active
        setIsConfirmOpen(true);
      },
      [isDeleting, isActive],
    );

    const cancelDelete = useCallback((e?: React.MouseEvent) => {
      e?.stopPropagation();
      setIsConfirmOpen(false);
    }, []);

    const confirmDelete = useCallback(
      async (e: React.MouseEvent) => {
        e.stopPropagation();
        // istanbul ignore next
        if (isDeleting || isActive) return; // Block execution if active

        setIsConfirmOpen(false);
        setIsDeleting(true);
        try {
          await deleteBackgroundImage({ imageId: image.id });
        } catch (error) {
          console.error('Failed to delete background image', error);
        } finally {
          setIsDeleting(false);
        }
      },
      [deleteBackgroundImage, isDeleting, image, isActive],
    );

    const isBusy = isApplying || isLoading || isDeleting;

    return (
      <Box
        as="button"
        onClick={isBusy ? undefined : onClick}
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
        w="100%"
        h="100%"
        overflow="hidden"
        borderRadius="0.3rem"
        sx={{
          '&:hover .delete': {
            // Hide delete icon on hover if deleting, confirming, or if card is currently active
            display: isDeleting || isConfirmOpen || isActive ? 'none' : 'flex',
          },
        }}
      >
        {(isLoading || isDeleting) && (
          <Center position="absolute" top="0" left="0" w="100%" h="100%" bg="rgba(0, 0, 0, 0.5)" zIndex="1">
            <Spinner size="sm" color="white" thickness="2px" />
          </Center>
        )}

        <Box
          as="img"
          src={image.url}
          alt={image.label}
          w="100%"
          h="100%"
          sx={{
            objectFit: 'cover',
            display: 'block',
            filter: isBusy ? 'blur(4px)' : 'none',
            transition: 'filter 0.2s ease',
          }}
        />

        {!image?.preExisting && !isActive && !isConfirmOpen && (
          <Box
            as="button"
            position="absolute"
            top="4px"
            right="4px"
            w="28px"
            h="28px"
            borderRadius="full"
            bg="red.500"
            color="white"
            fontSize="10px"
            display="none"
            alignItems="center"
            justifyContent="center"
            cursor={isDeleting ? 'default' : 'pointer'}
            className="delete"
            onClick={requestDelete}
            _hover={{ bg: 'red.600' }}
            outline="none"
            opacity={isDeleting ? 0.5 : 1}
            pointerEvents={isDeleting ? 'none' : 'auto'}
            zIndex="2"
          >
            <Icon as={RiDeleteBinFill} width="16px" height="16px" />
          </Box>
        )}

        {/* Simpler Custom Confirmation Overlay */}
        {isConfirmOpen && (
          <Box
            position="absolute"
            top="0"
            left="0"
            w="100%"
            h="100%"
            bg="rgba(0, 0, 0, 0.8)"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            zIndex="3"
            onClick={/* istanbul ignore next */ (e) => e.stopPropagation()} // Prevent card click while confirming
          >
            <Box color="white" fontSize="xs" mb={2} fontWeight="medium">
              {t('customBackgroundEffects.deleteImageTitle')}
            </Box>
            <ButtonGroup size="xs">
              <Button variant="ghost" color="white" _hover={{ bg: 'whiteAlpha.300' }} onClick={cancelDelete}>
                {t('customBackgroundEffects.deleteImageCancel')}
              </Button>
              <Button colorScheme="red" onClick={confirmDelete}>
                {t('customBackgroundEffects.deleteImageConfirm')}
              </Button>
            </ButtonGroup>
          </Box>
        )}
      </Box>
    );
  }),
);
